/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import * as Services from '@services';
import {
  LogVehicleSearchInput,
  PagedServiceHistory,
  SearchServiceHistoryInput,
  ServiceHistory,
  ServiceHistoryDetails,
  ServiceHistoryListItem,
  VehicleHistorySearch,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class ServiceHistoryStore {
  serviceHistory: ServiceHistory | undefined;
  serviceHistories: Map<string, ServiceHistory> = new Map();
  loading = false;
  totalCount = 0;
  skipCount = 0;
  currentPage = 1;
  maxResultCount = 15;

  // Search workspace state
  searchResults: ServiceHistoryListItem[] = [];
  searchTotalCount = 0;
  searchLoadedCount = 0;
  searchInput: SearchServiceHistoryInput | undefined;
  searchExecuted = false;
  searchLoading = false;
  vehicleHistoryDetails: ServiceHistoryDetails | undefined;
  vehicleHistoryDetailsLoading = false;

  // Recent searches (per-tenant)
  recentSearches: VehicleHistorySearch[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(request: PagedServiceHistory) {
    this.setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.getAll(request);
      runInAction(() => {
        this.serviceHistories.clear();
        result.items.forEach((item) => {
          this.serviceHistories.set(item.id, item);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async get(entityDto: Services.EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.get(entityDto);
      runInAction(() => {
        this.serviceHistory = result;
      });
      this.setLoading(false);
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async create(request: Services.CreateServiceHistory) {
    this.setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.create(request);
      runInAction(() => {
        if (result.reminderServices?.length > 0 && this.skipCount === 0) {
          const historiesArray = Array.from(this.serviceHistories.entries());

          if (historiesArray.length > 9) {
            historiesArray.pop();
          }
          this.serviceHistories = new Map([[result.id, result], ...historiesArray]);
        }
      });
      this.setLoading(false);
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return undefined;
    }
  }

  async sendServiceHistoryEmail(id: string, email?: string, fullName?: string) {
    this.setLoading(true);
    try {
      await Services.ServiceHistoryService.sendServiceHistoryEmail(id, email, fullName);
      this.setLoading(false);
      message.success({ content: 'Service history email sent successfully!' });
    } catch (error: any) {
      this.setLoading(false);
      message.error(error?.response?.data?.error?.message || 'Failed to send email');
    }
  }

  async update(request: Services.UpdateServiceHistory) {
    this.setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.update(request);
      runInAction(() => {
        if (result) {
          this.serviceHistories.set(result.id, result);
          this.serviceHistory = result;
        } else {
          this.serviceHistory = undefined;
          this.serviceHistories.delete(request.id);
        }
      });
      this.setLoading(false);
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return undefined;
    }
  }

  setLoading(loading: boolean) {
    runInAction(() => {
      this.loading = loading;
    });
  }

  handlePageChange = (page: any) => {
    runInAction(() => {
      this.maxResultCount = page.pageSize;
      this.skipCount = (page.current - 1) * page.pageSize;
      this.currentPage = page.current;
    });
  };

  clearServiceHistory() {
    runInAction(() => {
      this.serviceHistory = undefined;
    });
  }

  clearServiceHistories() {
    runInAction(() => {
      this.serviceHistories.clear();
    });
  }

  resetServiceHistories() {
    runInAction(() => {
      this.serviceHistories.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  }

  async runSearch(input: SearchServiceHistoryInput, opts?: { append?: boolean }) {
    runInAction(() => {
      this.searchLoading = true;
      if (!opts?.append) {
        this.searchResults = [];
        this.searchLoadedCount = 0;
        this.searchTotalCount = 0;
        this.searchExecuted = true;
      }
      this.searchInput = input;
    });
    try {
      const result = await Services.ServiceHistoryService.search(input);
      runInAction(() => {
        this.searchResults = opts?.append
          ? [...this.searchResults, ...result.items]
          : result.items;
        this.searchLoadedCount = this.searchResults.length;
        this.searchTotalCount = result.totalCount;
      });
    } catch (err) {
      console.error('runSearch failed', err);
    } finally {
      runInAction(() => {
        this.searchLoading = false;
      });
    }
  }

  async logSearch(input: LogVehicleSearchInput) {
    try {
      await Services.ServiceHistoryService.logSearch(input);
      await this.getRecentSearches();
    } catch (err) {
      console.error('logSearch failed', err);
    }
  }

  async getRecentSearches() {
    try {
      const result = await Services.ServiceHistoryService.getRecentSearches();
      runInAction(() => {
        this.recentSearches = result;
      });
    } catch (err) {
      console.error('getRecentSearches failed', err);
    }
  }

  async getVehicleHistoryDetails(id: string) {
    runInAction(() => {
      this.vehicleHistoryDetailsLoading = true;
    });
    try {
      const result = await Services.ServiceHistoryService.getDetails(id);
      runInAction(() => {
        this.vehicleHistoryDetails = result;
      });
    } catch (err) {
      console.error('getVehicleHistoryDetails failed', err);
    } finally {
      runInAction(() => {
        this.vehicleHistoryDetailsLoading = false;
      });
    }
  }

  resetSearch = () => {
    runInAction(() => {
      this.searchResults = [];
      this.searchTotalCount = 0;
      this.searchLoadedCount = 0;
      this.searchInput = undefined;
      this.searchExecuted = false;
    });
  };

  clearVehicleHistoryDetails = () => {
    runInAction(() => {
      this.vehicleHistoryDetails = undefined;
    });
  };
}

export default ServiceHistoryStore;
